import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { CelesteHostToolsConstants } from './celeste-host-tools.constants';
import { HostTool, HostToolsProvider } from './host-tool.model';

type ReportType = 'pdf_report' | 'report_builder';

interface CreateReportArgs {
  reportType: ReportType;
}

interface ReportTypeConfig {
  route: string;
  buttonSelector: string;
  label: string;
}

const REPORT_TYPE_CONFIG: Record<ReportType, ReportTypeConfig> = {
  pdf_report: {
    route: '/reporting/list?type=pdf',
    buttonSelector: '[celeste-host-tool="create-btn-report-builder"]',
    label: 'PDF report',
  },
  report_builder: {
    route: '/reporting/list?type=builder',
    buttonSelector: '[celeste-host-tool="create-btn-report-builder"]',
    label: 'Report Builder',
  },
};

const ELEMENT_POLL_INTERVAL_MS = 100;
/** Reports/Report Builder are lazy-loaded, so the marker button may render a beat after navigation resolves. */
const ELEMENT_POLL_TIMEOUT_MS = 3000;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Builds the `create_report` Celeste host tool: navigates to the requested report page, then clicks
 * its "New Report" button — marked with a `celeste-host-tool` attribute — the same way a user would.
 *
 * There are two distinct report features in this app; the tool description tells the agent to ask the
 * user which one they mean when it's ambiguous, rather than guessing.
 */
@Injectable({
  providedIn: 'root',
})
export class CreateReportHostToolService implements HostToolsProvider {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  public getTool(): HostTool<CreateReportArgs, { clicked: boolean; reportType: ReportType }> {
    return {
      name: 'create_report',
      description:
        'Opens the "create new report" dialog for a PDF report (/reporting/list?type=pdf) or a Report Builder report (/reporting/list?type=builder). ' +
        CelesteHostToolsConstants.REPORT_TYPE_AMBIGUITY_DISCLAIMER,
      inputSchema: {
        type: 'object',
        properties: {
          reportType: {
            type: 'string',
            enum: ['pdf_report', 'report_builder'],
            description:
              '"pdf_report" for a PDF report (/reporting/list?type=pdf), "report_builder" for a Report Builder report (/reporting/list?type=builder).',
          },
        },
        required: ['reportType'],
      },
      handler: (args) => this.createReport(args),
      timeoutMs: 15000,
    };
  }

  private async createReport(args: CreateReportArgs): Promise<{ clicked: boolean; reportType: ReportType }> {
    const config = REPORT_TYPE_CONFIG[args.reportType];

    if (!config) {
      throw new Error(`Unknown report type "${args.reportType}".`);
    }

    if (!this.router.url.startsWith(config.route)) {
      await this.router.navigateByUrl(config.route);
    }

    const marker = await this.waitForElement(config.buttonSelector, ELEMENT_POLL_TIMEOUT_MS);
    const button = marker?.querySelector('button');

    if (!button) {
      throw new Error(`Could not find the "New Report" button for ${config.label}.`);
    }

    button.click();

    return { clicked: true, reportType: args.reportType };
  }

  private async waitForElement(selector: string, timeoutMs: number): Promise<HTMLElement | null> {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      const element = this.document.querySelector<HTMLElement>(selector);

      if (element) {
        return element;
      }

      await wait(ELEMENT_POLL_INTERVAL_MS);
    }

    return null;
  }
}
